import { useMutation, useQuery, useQueryClient } from "react-query";
import type { ServoTypDaten, SetzeServoTyp } from "../../api";
import { createId, useGetApi } from "../../data";
import { ServoTypFilterType } from "./ServoTypFilter";

export function useServoTypen({
  seite,
  max,
  filter,
}: { seite?: number; max?: number; filter?: ServoTypFilterType } = {}) {
  const getApi = useGetApi();

  return useQuery(["servo-typen", { seite, max, filter }], async () =>
    (await getApi()).servo.sucheServoTypen(filter?.suche, seite, max)
  ).data!;
}

export function useServoTyp(id: string) {
  const getApi = useGetApi();

  return useQuery(["servo-typen", id], async () =>
    (await getApi()).servo.getServoTyp(id)
  ).data!;
}

export function useServoTypDaten(input: string | ServoTypDaten): ServoTypDaten {
  const getApi = useGetApi();

  return useQuery(["servo-typen", input], async () =>
    typeof input === "string"
      ? (await getApi()).servo.getServoTyp(input)
      : input
  ).data!;
}

export function useSetzeServoTyp() {
  const getApi = useGetApi();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, daten }: { id?: string; daten: SetzeServoTyp }) =>
      (await getApi()).servo.setzeServoTyp(id ?? createId(), daten),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries("servo-typen");
      },
    }
  );
}

export function useGetTestSzenarienForServoTyp({
  id,
  seite,
  max,
}: {
  id: string;
  seite?: number;
  max?: number;
}) {
  const getApi = useGetApi();
  return useQuery(
    ["servo-typ-test-szenarien-assigned", id, { seite, max }],
    async () =>
      (await getApi()).servo.getTestSzenarienForServoTyp(
        id,
        undefined,
        seite,
        max
      )
  ).data!;
}

export function useAddTestSzenarioToServoTyp() {
  const getApi = useGetApi();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      testSzenarioId,
      servoTypId,
    }: {
      testSzenarioId: string;
      servoTypId: string;
    }) =>
      (await getApi()).servo.addTestSzenarioForServoTyp(servoTypId, {
        testSzenarioId,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["servo-typ-test-szenarien-all"]);
        await queryClient.invalidateQueries([
          "servo-typ-test-szenarien-assigned",
        ]);
      },
    }
  );
}

export function useRemoveTestSzenarioFromServoTyp() {
  const getApi = useGetApi();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      testSzenarioId,
      servoTypId,
    }: {
      testSzenarioId: string;
      servoTypId: string;
    }) =>
      (await getApi()).servo.deleteTestSzenarioForServoTyp(servoTypId, {
        testSzenarioId,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          "servo-typ-test-szenarien-assigned",
        ]);
        await queryClient.invalidateQueries(["servo-typ-test-szenarien-all"]);
      },
    }
  );
}

export function useGetUnassignedTestSzenarien({
  id,
  seite,
  max,
}: {
  id: string;
  seite?: number;
  max?: number;
}) {
  const getApi = useGetApi();

  return useQuery(
    ["servo-typ-test-szenarien-all", id, { seite, max }],
    async () =>
      (await getApi()).servo.getUnassignedTestSzenarienForServoTyp(
        id,
        seite,
        max
      )
  ).data!;
}
